<template>
  <div class="container">
    <div class="is-relative box has-background-white form">
      <img class="logo" src="/images/gant-travel.png" alt="Gant Travel" />
      <h2 class="is-flex is-align-items-center is-size-4">
        <b-icon icon="card-account-details-outline" class="mr-2" />
        {{
          this.isLoading.get ? "Loading Profile Details..." : "Profile Details"
        }}
      </h2>
      <hr />
      <div v-if="isLoading.get">
        <b-skeleton width="20%" animated />
        <b-skeleton width="40%" animated />
        <b-skeleton width="80%" animated />
        <b-skeleton animated />
      </div>
      <div v-else-if="profile">
        <div
          v-for="(columns, index) in rows"
          :key="`columns-${index}`"
          class="columns"
        >
          <div
            v-for="(column, index) in columns"
            :key="`column-${index}`"
            class="column"
          >
            <b-field :label="column.display">
              <b-select
                v-if="column.isSelect"
                v-model="profile[column.value]"
                :placeholder="column.display"
                expanded
              >
                <option
                  v-for="option in column.options"
                  :value="option.value"
                  :key="option.value"
                >
                  {{ option.display }}
                </option>
              </b-select>
              <b-datepicker
                v-else-if="column.isDate"
                v-model="profile[column.value]"
                :placeholder="column.display"
                icon="calendar-today"
                icon-right-clickable
                trap-focus
              />
              <b-input
                v-else
                v-model="profile[column.value]"
                :placeholder="column.display"
                @input="checkError"
                @keyup.native.enter="UpdateProfile"
              />
            </b-field>
          </div>
        </div>
      </div>
      <div v-if="error" class="mt-4">
        <small class="has-text-danger">
          <b>Error: </b><em>{{ error }}</em>
        </small>
      </div>
      <hr />
      <b-button
        :disabled="isDisabled"
        :loading="isLoading.post"
        class="button is-primary is-fullwidth"
        @click="updateProfile"
      >
        Update Profile
      </b-button>
    </div>
    <b-modal v-model="isModal" :can-cancel="false" :width="420"> 
      <div
        class="is-flex is-justify-content-space-between box has-background-white"
      >
        <p>
          <b-icon icon="check" type="is-success" class="mr-2" />
          Your profile was successfully updated!
        </p>
        <button type="button" class="delete" @click="isModal = false"></button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      error: "",
      profile: null,
      isModal: false,
      isLoading: { get: false, post: false },
      rows: [
        [
          { value: "firstName", display: "First Name", isRequired: true },
          { value: "middleName", display: "Middle Name", isRequired: false },
          { value: "lastName", display: "Last Name", isRequired: true },
        ],
        [
          {
            value: "dateOfBirth",
            display: "Date of Birth",
            isRequired: true,
            isDate: true,
          },
        ],
        [
          {
            value: "gender",
            display: "Gender",
            isRequired: true,
            isSelect: true,
            options: [
              { value: "Male", display: "Male" },
              { value: "Female", display: "Female" },
              { value: "X", display: "Unspecified Gender" },
              { value: "U", display: "Undisclosed Gender" },
            ],
          },
        ],
        //[{ value: "email", display: "Email", isRequired: true }],
        [
          { value: "mobilePhone", display: "Mobile Phone", isRequired: true },
          { value: "homePhone", display: "Home Phone", isRequired: false },
          { value: "workPhone", display: "Work Phone (direct dial)", isRequired: false },
        ],
      ],
    };
  },
  computed: {
    isDisabled() {
      return this.error || this.isLoading.get || this.isLoading.post;
    },
  },
  async created() {
    this.profile = await this.getProfile();
    this.checkError();
  },
  methods: {
    async getProfile() {
      this.isLoading.get = true;

      const { id, email } = this.$route.params;
      const url = "profile/self-service/contact/show";
      const profile = await this.$app.huddleLabsServerCall(url, { id, email });

      this.isLoading.get = false;

      window.scrollTo(0, 0);

      if (!profile.success) {
        return null;
      }

      if (profile?.dateOfBirth) {
        profile.dateOfBirth = new Date(profile?.dateOfBirth.replace(/-/g, "/"));
      }

      return profile || null;
    },
    async updateProfile() {
      this.isLoading.post = true;

      const id = this.$route.params.id;
      const data = {
        id,
        email: this.profile.email,
        gender: this.profile.gender,
        lastName: this.profile.lastName,
        workPhone: this.profile.workPhone,
        homePhone: this.profile.homePhone,
        firstName: this.profile.firstName,
        middleName: this.profile.middleName,
        mobilePhone: this.profile.mobilePhone,
        dateOfBirth: this.profile.dateOfBirth,
      };
      const url = "profile/self-service/contact/update";
      const response = await this.$app.huddleLabsServerCall(url, data);

      this.isLoading.post = false;

      if (response.errors?.length) {
        this.error = response.errors[0].title;
        return;
      }

      this.isModal = true;
      this.profile = await this.getProfile();
    },
    checkError() {
      this.error = "";

      if (!this.profile) {
        return (this.error = "Profile not found.");
      }

      for (const row of this.rows) {
        for (const column of row) {
          if (!this.profile[column.value] && column.isRequired) {
            return (this.error = `The ${column.display} field is required`);
          }
        }
      }
    },
  },
};
</script>

<style scoped>
hr {
  margin-top: 1rem;
}
.form {
  width: 650px;
  max-width: 100%;
}
.container {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100% !important;
}
.logo {
  display: block;
  max-width: 250px;
  margin: 0px auto;
}
</style>
